// Enter Scores flow (Devin 2026-10-21): pick a competition, pick a judge, type
// the scores into one line of boxes, check them on a confirmation screen,
// then print the judge's sign-off sheet and mark it signed. Once a stage's
// sheets are all signed the competition offers its next step: the recall
// (print, publish) after round 2, the results (print, wait for the
// announcement, publish) at the end. Pure helpers; the screen is
// OireachtasTabulation.jsx. `row` is one competition of the Status payload
// (GET /oireachtas/:id/tabulation-status).

export const isMultiRound = (row) => (row?.competition?.rounds || 1) > 1;

// The rounds before the recall: 1 and 2 of a multi-round competition, the
// only round otherwise.
export const preRecallRounds = (row) => (isMultiRound(row) ? [1, 2] : [1]);

const roundRow = (row, round) => (row?.roundRows || []).find((r) => r.round === round);

// Every judge sheet of the round signed off.
export const roundSigned = (row, round) => {
  const rr = roundRow(row, round);
  return !!rr && rr.slots.length > 0 && rr.slots.every((s) => s.signOff === "signed_off");
};

// A round can take scores once its dancers are known: rounds 1-2 always,
// a recall round only after the recall is published.
export const roundOpen = (row, round) => !(isMultiRound(row) && round >= 3) || !!row?.recallPublished;

// Where the competition stands, as the next thing the tabulator does.
//   { step: "no_draw" }
//   { step: "scores", round }      sheets still to enter or sign for `round`
//   { step: "recall" }             rounds 1-2 signed: print + publish the recall
//   { step: "results" }            every round signed: print, announce, publish
//   { step: "done" }               results published
// A publication corrected after it went up (an admin correction) stays
// pending until it is published again: { step: ..., republish: true } once its
// sheets are signed again.
export function compStep(row) {
  if (!row?.draw?.locked) return { step: "no_draw" };
  const corrected = row.corrected || {};
  const rounds = row.competition.rounds || 1;
  const before = preRecallRounds(row);
  const open = before.find((r) => !roundSigned(row, r));
  const later = () => {
    for (let r = before.length + 1; r <= rounds; r++) if (!roundSigned(row, r)) return r;
    return null;
  };
  if (row.resultsPublished) {
    if (!corrected.results) return { step: "done" };
    const unsigned = open || later();
    return unsigned ? { step: "scores", round: unsigned, republish: true } : { step: "results", republish: true };
  }
  if (open) return { step: "scores", round: open, ...(corrected.recall && row.recallPublished ? { republish: true } : {}) };
  if (isMultiRound(row) && (!row.recallPublished || corrected.recall)) {
    return { step: "recall", ...(row.recallPublished ? { republish: true } : {}) };
  }
  const r = later();
  if (r) return { step: "scores", round: r };
  return { step: "results" };
}

// A tabulator has checked this stage's overall result, and it still holds
// (nothing changed since). stage: "recall" | "results".
export const stageChecked = (row, stage) => !!row?.checks?.[stage]?.checked;

// Changing a score in this round changes something already public: the
// results, or the recall (rounds 1-2).
export const roundPublished = (row, round) =>
  !!row && (!!row.resultsPublished || (!!row.recallPublished && isMultiRound(row) && round <= 2));

// The round Enter Scores opens on for a competition.
export function defaultRound(row) {
  const s = compStep(row);
  if (s.step === "scores") return s.round;
  if (s.step === "recall") return 2;
  return row?.competition?.rounds || 1;
}

// One short line for the competition picker.
export function stepLabel(row) {
  const s = compStep(row);
  if (s.step === "no_draw") return "draw not locked";
  if (s.republish) return "corrected — publish again";
  if (s.step === "done") return "results published";
  if (s.step === "recall") return "recall ready";
  if (s.step === "results") return "results ready";
  const rr = roundRow(row, s.round);
  const signed = rr ? rr.slots.filter((x) => x.signOff === "signed_off").length : 0;
  const total = rr ? rr.slots.length : 0;
  return `round ${s.round}: ${signed}/${total} signed`;
}

// Judge sheet states in a round, by slot letter: "missing" (nothing entered),
// "entered", "printed" or "signed_off".
export function sheetState(row, round, slot) {
  const s = (roundRow(row, round)?.slots || []).find((x) => x.slot === slot);
  if (!s) return "missing";
  if (s.signOff === "missing") return s.stored > 0 ? "entered" : "missing";
  return s.signOff;
}

export const SHEET_LABEL = {
  missing: "Not entered",
  entered: "Entered — print sign-off",
  printed: "Printed — awaiting sign-off",
  signed_off: "Signed off",
};

// The boxes for one judge's sheet: every dancer due in the round, in running
// order. Check-in has no part in it (Devin 2026-10-22: the judges' papers are
// the record of who danced) — a blank box is how an absent dancer is left
// out. Stored scores fill in (slot "" = a judge not yet seated).
export function boxesFromGrid(grid, slot) {
  if (!grid) return [];
  return grid.roster.map((r) => ({
      card: String(r.card),
      ref: r.ref,
      score: slot && r.cells[slot] ? String(r.cells[slot].score) : "",
      ftc: !!r.mishap?.zeroed,
    }));
}

// The boxes laid out like the judge's paper sheet (Devin 2026-10-28). A copy
// of the backend's helpers/oireachtasSheetLayout.js, which lays out the
// printed score sheet and the sign-off sheet — keep the three the same, so
// they line up row for row: up to SHEET_SINGLE_MAX dancers in one column; up
// to 50 in two, the left one filled first with half (rounded up); more than
// 50, pages of 50 (25 down the left, then 25 down the right). In running
// order. → pages of columns of { box, index } (index into boxes, for focus
// and edits).
export const SHEET_SINGLE_MAX = 12;
export const SHEET_COLUMN_ROWS = 25;
export function sheetPages(boxes) {
  const n = boxes.length;
  const cells = (from, to) => boxes.slice(from, to).map((box, k) => ({ box, index: from + k }));
  if (n === 0) return [];
  if (n <= SHEET_SINGLE_MAX) return [[cells(0, n)]];
  if (n <= 2 * SHEET_COLUMN_ROWS) {
    const rows = Math.ceil(n / 2);
    return [[cells(0, rows), cells(rows, n)]];
  }
  const pages = [];
  for (let start = 0; start < n; start += 2 * SHEET_COLUMN_ROWS) {
    const mid = Math.min(start + SHEET_COLUMN_ROWS, n);
    pages.push([cells(start, mid), cells(mid, Math.min(start + 2 * SHEET_COLUMN_ROWS, n))]);
  }
  return pages;
}

// Today's competitions (Devin 2026-10-28): Enter Scores lists only the
// competitions with a round on today's stage schedule. dayKey is today as the
// schedule writes days ("YYYY-MM-DD", see eventDayKey). → a Set of
// competition ids, or null when the schedule has nothing on today (no
// schedule yet, or not an event day): then every competition is listed.
export function scheduledToday(schedule, dayKey) {
  const day = (schedule?.days || []).find((d) => d.day === dayKey);
  const ids = new Set();
  for (const stage of day?.stages || []) {
    for (const item of stage.items || []) {
      if (item.type === "round" && item.competition_id) ids.add(String(item.competition_id));
    }
  }
  return ids.size ? ids : null;
}

// Today in the event's time zone, as a schedule day key ("YYYY-MM-DD").
export function eventDayKey(timeZone, now = new Date()) {
  const opts = { year: "numeric", month: "2-digit", day: "2-digit" };
  let parts;
  try {
    parts = new Intl.DateTimeFormat("en-US", { ...opts, timeZone: timeZone || undefined }).formatToParts(now);
  } catch (e) {
    parts = new Intl.DateTimeFormat("en-US", opts).formatToParts(now); // unknown zone: this device's
  }
  const part = (type) => parts.find((p) => p.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

// A sheet's scores as one comparable string — the same form as the server's
// columnFingerprint ("ref:score" sorted). refByCard maps cards to refs for
// boxes that carry only a card (a stored draft's baseline).
export function sheetFingerprint(boxes, refByCard = {}) {
  return (boxes || [])
    .filter((b) => String(b.score ?? "").trim() !== "")
    .map((b) => `${String(b.ref || refByCard[String(b.card)] || "")}:${Number(String(b.score).trim())}`)
    .sort()
    .join(",");
}

export const boxesChanged = (a, b) =>
  a.length !== b.length ||
  a.some((x, i) => x.score.trim() !== b[i].score.trim() || x.ftc !== b[i].ftc);

// Every problem with a typed sheet, not just the first, so the confirmation
// screen can list them all.
export function sheetProblems(boxes) {
  const problems = [];
  for (const b of boxes) {
    const raw = b.score.trim();
    if (raw === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      problems.push({ card: b.card, message: `#${b.card}: "${raw}" is not a score from 0 to 100` });
    }
  }
  return problems;
}

export const blanks = (boxes) => boxes.filter((b) => b.score.trim() === "" && !b.ftc).map((b) => b.card);

// What Confirm sends: scores to store, stored scores the tabulator erased,
// and FTC marks that changed.
export function sheetChanges(boxes, original) {
  const before = Object.fromEntries(original.map((b) => [b.card, b]));
  const upserts = boxes
    .filter((b) => b.score.trim() !== "")
    .map((b) => ({ ref: b.ref, score: Number(b.score), comment: false }));
  const clears = boxes
    .filter((b) => b.score.trim() === "" && before[b.card] && before[b.card].score.trim() !== "")
    .map((b) => ({ ref: b.ref, score: null, comment: false }));
  const ftc = boxes
    .filter((b) => before[b.card] && b.ftc !== before[b.card].ftc)
    .map((b) => ({ ref: b.ref, ftc: b.ftc }));
  return { upserts, clears, ftc };
}

// ---- unsaved drafts ---------------------------------------------------------------
// Typed but unconfirmed scores are kept in the browser (localStorage) so a
// page change, reload or crash can't lose them; reopening the judge's sheet
// restores them. Keyed by event, competition, round and judge.
export const DRAFT_PREFIX = "tbDraft:";
export const draftKey = (eventId, compId, round, personId) =>
  `${DRAFT_PREFIX}${eventId}:${compId}:${round}:${personId}`;

// The sheet as stored on the server, with only what the tabulator changed in
// the draft laid over it card by card: a field equal to the draft's baseline
// (the sheet as it was when typing started) keeps the server's current value,
// so a later change from elsewhere — e.g. an FTC mark saved through another
// judge's sheet — isn't undone. A card no longer on the sheet is dropped.
export function mergeDraft(fresh, draftBoxes, baseBoxes) {
  const typed = Object.fromEntries((draftBoxes || []).map((b) => [String(b.card), b]));
  const base = Object.fromEntries((baseBoxes || []).map((b) => [String(b.card), b]));
  return fresh.map((b) => {
    const d = typed[b.card];
    if (!d) return b;
    const was = base[b.card];
    const score = String(d.score ?? "");
    const ftc = !!d.ftc;
    return {
      ...b,
      score: !was || score.trim() !== String(was.score ?? "").trim() ? score : b.score,
      ftc: !was || ftc !== !!was.ftc ? ftc : b.ftc,
    };
  });
}

// storage: a Storage-like object (localStorage). Every call is guarded: a
// private window or blocked storage just means no drafts.
export function readDraft(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}
export function writeDraft(storage, key, draft) {
  try {
    storage.setItem(key, JSON.stringify(draft));
  } catch (e) {
    /* storage unavailable */
  }
}
export function dropDraft(storage, key) {
  try {
    storage.removeItem(key);
  } catch (e) {
    /* storage unavailable */
  }
}
export function listDrafts(storage, eventId) {
  const out = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(`${DRAFT_PREFIX}${eventId}:`)) {
        const d = readDraft(storage, key);
        if (d) out.push({ key, ...d });
      }
    }
  } catch (e) {
    return [];
  }
  return out.sort((a, b) => String(a.code).localeCompare(String(b.code)) || a.round - b.round);
}
